import React, { FC } from "react";
import { BrowserRouter as Router, Routes, Route, Outlet } from "react-router-dom";
import { ConfigProvider, Layout } from "antd";

import { isChrome, isSafari, isFirefox, isEdge, isMobile } from "react-device-detect";

import { NewsContextProvider } from "./context/NewsContext/NewsContext";
import { ImagesProvider } from "./context/ImagesProvider";
import { EventContextProvider } from "./context/EventContext/EventContext";
import { EventTagContextProvider } from "./context/EventTagContext/EventTagContext";
import { EventApplicationContextProvider } from "./context/EventApplicationContext/EventApplicationContext";
import { DiscoveryContextProvider } from "./context/DiscoveryContext/DiscoveryContext";
import { CDNFileContextProvider } from "./context/CDNFileContext/CDNFileContext";
import { DocCategoryContextProvider } from "./context/DocumentCategoryContext/DocumentCategoryContext";
import { MobileAppVersionContextProvider } from "./context/MobileAppVersionContext/MobileAppVersionContext";
import { BannerContextProvider } from "./context/BannerContext/BannerContext";
import { BannerMobileContextProvider } from "./context/BannerMobileContext/BannerMobileContext";
import { DocumentContextProvider } from "./context/DocumentContext/DocumentContext";
import { VolunteersHighlightContextProvider } from "./context/VolunteersHighlightContext/VolunteersHighlightContext";
import { VolunteersAboutContextProvider } from "./context/VolunteersAboutContext/VolunteersAboutContext";
import { VolunteersFAQContextProvider } from "./context/VolunteersFAQContext/VolunteersFAQContext";
import { VolunteersExperienceContextProvider } from "./context/VolunteersExperienceContext/VolunteersExperienceContext";
import { VolunteersGalleryContextProvider } from "./context/VolunteersGalleryContext/VolunteersGalleryContext";
import { KvkkContextProvider } from "./context/KvkkContext/KvkkContext";

import MainHeader from "./components/HeaderComponent";
import Footer from "./components/Footer";
import ProtectedRoute from "./components/Routing";
import Login from "./components/Routing/Login";
import Sidebar from "./components/SidebarComponent";

import HomePage from "./pages/HomePage";
import AuthWarning from "./pages/Auth/AuthWarning";
import Logout from "./pages/Logout";

import MainContainer from "./App.style";
import { SidebarMenuProvider } from "./context/SidebarMenuProvider";
import useScreenWidth from "./utils/useScreenWidth";
import Warning from "./pages/Auth/Warning";
import MobileWarning from "./pages/Auth/MobileWarning";
import ErrorPage from "./pages/Error";

import AddNews from "./pages/AddNews";
import AddEvent from "./pages/AddEvent";
import EditNews from "./pages/EditNews";

import EditEvent from "./pages/EditEvent";
import NewsList from "./pages/NewsList";
import EventList from "./pages/EventList";
import EventParticipantList from "./pages/EventParticipantList";
import EventApplicationList from "./pages/EventApplicationList";
import EventTagList from "./pages/EventTagList";
import AddEventTag from "./pages/AddEventTag";
import EditEventTag from "./pages/EditEventTag";
import VolunteersHighlightList from "./pages/VolunteersHighlightList";
import AddVolunteersHighlight from "./pages/AddVolunteersHighlight";
import EditVolunteersHighlight from "./pages/EditVolunteersHighlight";
import VolunteersAbout from "./pages/VolunteersAbout";
import AddVolunteersAbout from "./pages/AddVolunteersAbout";
import EditVolunteersAbout from "./pages/EditVolunteersAbout";
import VolunteersFAQList from "./pages/VolunteersFAQList";
import AddVolunteersFAQ from "./pages/AddVolunteersFAQ";
import EditVolunteersFAQ from "./pages/EditVolunteersFAQ";
import VolunteersExperienceList from "./pages/VolunteersExperienceList";
import AddVolunteersExperience from "./pages/AddVolunteersExperience";
import EditVolunteersExperience from "./pages/EditVolunteersExperience";
import VolunteersGalleryList from "./pages/VolunteersGalleryList";
import AddVolunteersGallery from "./pages/AddVolunteersGallery";
import EditVolunteersGallery from "./pages/EditVolunteersGallery";
import KvkkList from "./pages/KvkkList";
import AddKvkk from "./pages/AddKvkk";
import EditKvkk from "./pages/EditKvkk";

import DiscoveryList from "./pages/DiscoveryList";
import AddDiscovery from "./pages/AddDiscovery";
import EditDiscovery from "./pages/EditDiscovery";

import CDNFileList from "./pages/CDNFileList";
import AddCDNFile from "./pages/AddCDNFile";
import EditCDNFile from "./pages/EditCDNFile";

import DocumentCategoryList from "./pages/DocumentCategoryList";
import AddDocumentCategory from "./pages/AddDocumentCategory";
import EditDocumentCategory from "./pages/EditDocumentCategory";

import MobileAppVersionList from "./pages/MobileAppVersionList";
import AddMobileAppVersion from "./pages/AddMobileAppVersion";
import EditMobileAppVersion from "./pages/EditMobileAppVersion";

import BannerList from "./pages/BannerList";
import AddBanner from "./pages/AddBanner";
import EditBanner from "./pages/EditBanner";
import OrderBanner from "./pages/OrderBanner";

import BannerMobileList from "./pages/BannerMobileList";
import AddBannerMobile from "./pages/AddBannerMobile";
import EditBannerMobile from "./pages/EditBannerMobile";
import OrderBannerMobile from "./pages/OrderBannerMobile";

import DocumentList from "./pages/DocumentList";
import AddDocument from "./pages/AddDocument";
import EditDocument from "./pages/EditDocument";

interface LayoutContainerProps {
	children: React.ReactNode;
}

const WIDTH_MESSAGE = "ScreenWidthMessage";
const BROWSER_MESSAGE = "ScreenBrowserMessage";
const MOBILE_MESSAGE = "ScreenMobileMessage";

const ProtectedLayout: FC<LayoutContainerProps> = ({ children }) => {
	return (
		<ImagesProvider>
			<SidebarMenuProvider>
				<Layout style={{ minHeight: "100vh" }}>
					<MainHeader />
					<MainContainer>
						<Sidebar />
						{children}
					</MainContainer>
					<Footer />
				</Layout>
			</SidebarMenuProvider>
		</ImagesProvider>
	);
};

const App: FC = () => {
	const isScreenWidthSmall = useScreenWidth();

	if (isMobile) {
		return <MobileWarning queryString={MOBILE_MESSAGE} />;
	} else if (isChrome || isSafari || isFirefox || isEdge) {
		if (isScreenWidthSmall) {
			return <Warning queryString={WIDTH_MESSAGE} />;
		}
	} else {
		return <Warning queryString={BROWSER_MESSAGE} />;
	}


	return (
		<ConfigProvider
			theme={{
				token: {
					fontFamily: "Roboto, sans-serif",
					lineHeight: 1.38,
				},
			}}
		>
			<Router>
				<NewsContextProvider>
					<DiscoveryContextProvider>
						<EventContextProvider>
							<EventTagContextProvider>
								<EventApplicationContextProvider>
									<CDNFileContextProvider>
										<DocCategoryContextProvider>
											<DocumentContextProvider>
												<MobileAppVersionContextProvider>
													<BannerContextProvider>
														<BannerMobileContextProvider>
															<VolunteersHighlightContextProvider>
																<Routes>
																	<Route element={<ProtectedRoute />}>
																		<Route
																			path="/"
																			element={(
																				<ProtectedLayout>
																					<HomePage />
																				</ProtectedLayout>
																			)}
																		/>
																		<Route
																			path="/news"
																			element={(
																				<ProtectedLayout>
																					<NewsList />
																				</ProtectedLayout>
																			)}
																		/>
																		<Route
																			path="/add-news"
																			element={(
																				<ProtectedLayout>
																					<AddNews />
																				</ProtectedLayout>
																			)}
																		/>
																		<Route
																			path="/edit-news/:id"
																			element={(
																				<ProtectedLayout>
																					<EditNews />
																				</ProtectedLayout>
																			)}
																		/>

																		<Route
																			path="/events"
																			element={(
																				<ProtectedLayout>
																					<EventList />
																				</ProtectedLayout>
																			)}
																		/>
																		<Route
																			path="/event-applications"
																			element={(
																				<ProtectedLayout>
																					<EventApplicationList />
																				</ProtectedLayout>
																			)}
																		/>
																		<Route
																			path="/event-applications/:id/participants"
																			element={(
																				<ProtectedLayout>
																					<EventParticipantList />
																				</ProtectedLayout>
																			)}
																		/>
																		<Route
																			path="/events/add"
																			element={(
																				<ProtectedLayout>
																					<AddEvent />
																				</ProtectedLayout>
																			)}
																		/>
																		<Route
																			path="/events/edit/:id"
																			element={(
																				<ProtectedLayout>
																					<EditEvent />
																				</ProtectedLayout>
																			)}
																		/>
																		<Route
																			path="/event-tags"
																			element={(
																				<ProtectedLayout>
																					<EventTagList />
																				</ProtectedLayout>
																			)}
																		/>
																		<Route
																			path="/event-tags/add"
																			element={(
																				<ProtectedLayout>
																					<AddEventTag />
																				</ProtectedLayout>
																			)}
																		/>
																		<Route
																			path="/event-tags/edit/:id"
																			element={(
																				<ProtectedLayout>
																					<EditEventTag />
																				</ProtectedLayout>
																			)}
																		/>

																		<Route
																			path="/volunteers-highlights"
																			element={(
																				<ProtectedLayout>
																					<VolunteersHighlightList />
																				</ProtectedLayout>
																			)}
																		/>

																		<Route
																			path="/volunteers-highlights/add"
																			element={(
																				<ProtectedLayout>
																					<AddVolunteersHighlight />
																				</ProtectedLayout>
																			)}
																		/>

																		<Route
																			path="/volunteers-highlights/edit/:id"
																			element={(
																				<ProtectedLayout>
																					<EditVolunteersHighlight />
																				</ProtectedLayout>
																			)}
																		/>

																		{/* The about screens share one provider; it lives only while one of them is open. The layout
																		    stays the outer element, as on every other page, so React keeps the same header and sidebar
																		    when the user moves between these screens and the others. */}
																		<Route
																			element={(
																				<ProtectedLayout>
																					<VolunteersAboutContextProvider>
																						<Outlet />
																					</VolunteersAboutContextProvider>
																				</ProtectedLayout>
																			)}
																		>
																			<Route path="/volunteers-about" element={<VolunteersAbout />} />
																			<Route path="/volunteers-about/add" element={<AddVolunteersAbout />} />
																			<Route path="/volunteers-about/edit/:id" element={<EditVolunteersAbout />} />
																		</Route>

																		{/* The FAQ screens share one provider in the same way. */}
																		<Route
																			element={(
																				<ProtectedLayout>
																					<VolunteersFAQContextProvider>
																						<Outlet />
																					</VolunteersFAQContextProvider>
																				</ProtectedLayout>
																			)}
																		>
																			<Route path="/volunteers-faq" element={<VolunteersFAQList />} />
																			<Route path="/volunteers-faq/add" element={<AddVolunteersFAQ />} />
																			<Route path="/volunteers-faq/edit/:id" element={<EditVolunteersFAQ />} />
																		</Route>

																		{/* The volunteer experience screens share one provider in the same way. */}
																		<Route
																			element={(
																				<ProtectedLayout>
																					<VolunteersExperienceContextProvider>
																						<Outlet />
																					</VolunteersExperienceContextProvider>
																				</ProtectedLayout>
																			)}
																		>
																			<Route path="/volunteers-experiences" element={<VolunteersExperienceList />} />
																			<Route path="/volunteers-experiences/add" element={<AddVolunteersExperience />} />
																			<Route path="/volunteers-experiences/edit/:id" element={<EditVolunteersExperience />} />
																		</Route>

																		{/* The gallery screens share one provider in the same way. */}
																		<Route
																			element={(
																				<ProtectedLayout>
																					<VolunteersGalleryContextProvider>
																						<Outlet />
																					</VolunteersGalleryContextProvider>
																				</ProtectedLayout>
																			)}
																		>
																			<Route path="/volunteers-gallery" element={<VolunteersGalleryList />} />
																			<Route path="/volunteers-gallery/add" element={<AddVolunteersGallery />} />
																			<Route path="/volunteers-gallery/edit/:id" element={<EditVolunteersGallery />} />
																		</Route>

																		{/* The KVKK text screens share one provider in the same way. */}
																		<Route
																			element={(
																				<ProtectedLayout>
																					<KvkkContextProvider>
																						<Outlet />
																					</KvkkContextProvider>
																				</ProtectedLayout>
																			)}
																		>
																			<Route path="/kvkk" element={<KvkkList />} />
																			<Route path="/kvkk/add" element={<AddKvkk />} />
																			<Route path="/kvkk/edit/:id" element={<EditKvkk />} />
																		</Route>

																		<Route
																			path="/discovery"
																			element={(
																				<ProtectedLayout>
																					<DiscoveryList />
																				</ProtectedLayout>
																			)}
																		/>
																		<Route
																			path="/add-discovery"
																			element={(
																				<ProtectedLayout>
																					<AddDiscovery />
																				</ProtectedLayout>
																			)}
																		/>
																		<Route
																			path="/edit-discovery/:id"
																			element={(
																				<ProtectedLayout>
																					<EditDiscovery />
																				</ProtectedLayout>
																			)}
																		/>
																		<Route
																			path="/cdn"
																			element={(
																				<ProtectedLayout>
																					<CDNFileList />
																				</ProtectedLayout>
																			)}
																		/>
																		<Route
																			path="/add-cdn-file"
																			element={(
																				<ProtectedLayout>
																					<AddCDNFile />
																				</ProtectedLayout>
																			)}
																		/>
																		<Route
																			path="/edit-cdn-file/:id"
																			element={(
																				<ProtectedLayout>
																					<EditCDNFile />
																				</ProtectedLayout>
																			)}
																		/>
																		<Route
																			path="/doc-categories"
																			element={(
																				<ProtectedLayout>
																					<DocumentCategoryList />
																				</ProtectedLayout>
																			)}
																		/>
																		<Route
																			path="/add-doc-category"
																			element={(
																				<ProtectedLayout>
																					<AddDocumentCategory />
																				</ProtectedLayout>
																			)}
																		/>
																		<Route
																			path="/edit-doc-category/:id"
																			element={(
																				<ProtectedLayout>
																					<EditDocumentCategory />
																				</ProtectedLayout>
																			)}
																		/>
																		<Route
																			path="/documents/:id"
																			element={(
																				<ProtectedLayout>
																					<DocumentList />
																				</ProtectedLayout>
																			)}
																		/>
																		<Route
																			path="/add-document"
																			element={(
																				<ProtectedLayout>
																					<AddDocument />
																				</ProtectedLayout>
																			)}
																		/>
																		<Route
																			path="/edit-document/:id"
																			element={(
																				<ProtectedLayout>
																					<EditDocument />
																				</ProtectedLayout>
																			)}
																		/>
																		<Route
																			path="/mobile-app-versions"
																			element={(
																				<ProtectedLayout>
																					<MobileAppVersionList />
																				</ProtectedLayout>
																			)}
																		/>
																		<Route
																			path="/add-mobile-app-version"
																			element={(
																				<ProtectedLayout>
																					<AddMobileAppVersion />
																				</ProtectedLayout>
																			)}
																		/>
																		<Route
																			path="/edit-mobile-app-version/:id"
																			element={(
																				<ProtectedLayout>
																					<EditMobileAppVersion />
																				</ProtectedLayout>
																			)}
																		/>
																		<Route
																			path="/banners"
																			element={(
																				<ProtectedLayout>
																					<BannerList />
																				</ProtectedLayout>
																			)}
																		/>
																		<Route
																			path="/add-banner"
																			element={(
																				<ProtectedLayout>
																					<AddBanner />
																				</ProtectedLayout>
																			)}
																		/>
																		<Route
																			path="/edit-banner/:id"
																			element={(
																				<ProtectedLayout>
																					<EditBanner />
																				</ProtectedLayout>
																			)}
																		/>
																		<Route
																			path="/order-banners"
																			element={(
																				<ProtectedLayout>
																					<OrderBanner />
																				</ProtectedLayout>
																			)}
																		/>
																		<Route
																			path="/mobile-banners"
																			element={(
																				<ProtectedLayout>
																					<BannerMobileList />
																				</ProtectedLayout>
																			)}
																		/>
																		<Route
																			path="/add-mobile-banner"
																			element={(
																				<ProtectedLayout>
																					<AddBannerMobile />
																				</ProtectedLayout>
																			)}
																		/>
																		<Route
																			path="/edit-mobile-banner/:id"
																			element={(
																				<ProtectedLayout>
																					<EditBannerMobile />
																				</ProtectedLayout>
																			)}
																		/>
																		<Route
																			path="/order-mobile-banners"
																			element={(
																				<ProtectedLayout>
																					<OrderBannerMobile />
																				</ProtectedLayout>
																			)}
																		/>
																	</Route>
																	<Route path="/auth-warning" element={<AuthWarning />} />
																	<Route path="/accounts/login" element={<Login />} />
																	<Route path="/accounts/logout" element={<Logout />} />
																	<Route path="/error" element={<ErrorPage />} />
																</Routes>
															</VolunteersHighlightContextProvider>
														</BannerMobileContextProvider>
													</BannerContextProvider>
												</MobileAppVersionContextProvider>
											</DocumentContextProvider>
										</DocCategoryContextProvider>
									</CDNFileContextProvider>
								</EventApplicationContextProvider>
							</EventTagContextProvider>
						</EventContextProvider>
					</DiscoveryContextProvider>
				</NewsContextProvider>
			</Router>
		</ConfigProvider>
	);
};

export default App;
